import { useEffect, useMemo, useState } from 'react';
import { Skeleton } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { getDateFormat, getTimeFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { truncateStringWith3Dots } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  CONTENT_ACTIVITY_VIEWS,
  VIDEO_PLAY_STATUS,
  CONTENT_OCCURRENCE_KIND,
} from '@/constants/device';
import { exportContentActivityToPDF } from '@/utilities/helpers/fileHelper';
import ExportPdfButton from '@/Components/Common/ExportPdfButton';
import MainPage from '../../MainPage';
import { ContentActivityIcon } from '@/Components/Common/Icons';
import { HistoryOutlined } from '@ant-design/icons';
import { formatWatchDuration, formatPercent, getChannelInitials } from './ContentActivityUtils';
import ContentActivitySummary from './ContentActivitySummary';
import ContentActivityCharts from './ContentActivityCharts';
import useAppRegistryApps from '@/Components/Hooks/useAppRegistryApps';
import useIsDarkTheme from '@/Components/Hooks/useIsDarkTheme';
import { getChannelIconUrl } from '@/Components/Common/Icons/ChannelIcons';
import DiscoverLiveLogo from '@/Components/Common/Icons/discover-live.svg';
import EnrichFirstLogo from '@/Components/Common/Icons/enrich-first.svg';
import FaithFirstLogo from '@/Components/Common/Icons/faith-first.svg';
import MemoryLaneLogo from '@/Components/Common/Icons/memory-lane.svg';
import MusicFirstLogo from '@/Components/Common/Icons/music-first.svg';
import Spiro100Logo from '@/Components/Common/Icons/spiro-100.svg';
import StageAccessLogo from '@/Components/Common/Icons/stage-access.svg';
import ZinniaLogo from '@/Components/Common/Icons/zinnia.svg';
import '@/styles/ContentActivity.less';

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const HISTORY_PAGE_SIZE = 10;
const CHANNEL_LOGO_MAP = {
  'discover-live': DiscoverLiveLogo,
  'coro-health-enrich-first': EnrichFirstLogo,
  'coro-health-faith-first': FaithFirstLogo,
  'memory-lane-tv': MemoryLaneLogo,
  'coro-health-music-first': MusicFirstLogo,
  spiro100: Spiro100Logo,
  'stage-access': StageAccessLogo,
  'zinnia-tv': ZinniaLogo,
};

const getChannelLogoKey = (name) =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getRegistryNameKey = (name) =>
  String(name || '')
    .trim()
    .toLowerCase();

const ListContentActivity = (props) => {
  const {
    parentBread,
    model,
    loading,
    activeView,
    setActiveView,
    timezone,
    loginUserTimeformat,
    userInfo,
  } = props;
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const header = parentBread || [];
  const userTimezone = timezone || LocalTimezone;
  const targetUseruuid = userInfo?.useruuid || currentUser?.useruuid;

  const { data: appRegistryApps = [], error: appRegistryViewError } = useAppRegistryApps({
    useruuid: targetUseruuid,
  });
  const isDarkTheme = useIsDarkTheme();

  const launcherIconByName = useMemo(() => {
    const iconMap = {};

    appRegistryApps.forEach((app) => {
      // Prefer the icon matching the active theme; use the other API variant only when needed.
      // If neither is provided, renderChannelAvatar falls back to the bundled channel logo.
      const launcherIcon = isDarkTheme
        ? app?.metadata?.launcher_dark_icon || app?.metadata?.launcher_light_icon
        : app?.metadata?.launcher_light_icon || app?.metadata?.launcher_dark_icon;
      if (!launcherIcon) return;

      [app?.metadata?.name, app?.metadata?.launcher_label].forEach((name) => {
        const key = getRegistryNameKey(name);
        if (key) iconMap[key] = launcherIcon;
      });
    });

    return iconMap;
  }, [appRegistryApps, isDarkTheme]);

  useEffect(() => {
    if (appRegistryApps.length) {
      console.log('[ContentActivity] app-registry/view response:', appRegistryApps);
    }
  }, [appRegistryApps]);

  useEffect(() => {
    if (appRegistryViewError) {
      console.error('[ContentActivity] app-registry/view error:', appRegistryViewError);
    }
  }, [appRegistryViewError]);

  const historyByDate = useMemo(() => model?.historyByDate || [], [model?.historyByDate]);
  const historyEmpty = historyByDate.length === 0;

  const [visibleHistoryCount, setVisibleHistoryCount] = useState(HISTORY_PAGE_SIZE);
  const [failedLauncherIcons, setFailedLauncherIcons] = useState({});

  const totalHistoryCount = useMemo(
    () => historyByDate.reduce((sum, group) => sum + group.items.length, 0),
    [historyByDate]
  );

  // Trim historyByDate down to the first `visibleHistoryCount` items overall,
  // preserving date grouping, so the Summary tab only previews recent activity
  // by default instead of rendering a potentially very long month of rows.
  const visibleHistoryByDate = useMemo(() => {
    let remaining = visibleHistoryCount;
    const result = [];
    historyByDate.forEach((group) => {
      if (remaining <= 0) return;
      if (group.items.length <= remaining) {
        result.push(group);
        remaining -= group.items.length;
      } else {
        result.push({ ...group, items: group.items.slice(0, remaining) });
        remaining = 0;
      }
    });
    return result;
  }, [historyByDate, visibleHistoryCount]);

  const hasMoreHistory = totalHistoryCount > visibleHistoryCount;

  const renderChannelAvatar = (name, color) => {
    const channelKey = getRegistryNameKey(name);
    const launcherIcon = launcherIconByName[channelKey];
    const bundledIcon = getChannelIconUrl(name);
    const useBundledIcon = !launcherIcon || failedLauncherIcons[channelKey];
    const iconUrl = useBundledIcon ? bundledIcon : launcherIcon;

    if (iconUrl) {
      return (
        <div className="channel-avatar channel-avatar-with-logo">
          <img
            className="channel-avatar-logo"
            src={iconUrl}
            alt={`${name || 'Channel'} logo`}
            onError={(event) => {
              if (!useBundledIcon && bundledIcon) {
                setFailedLauncherIcons((previous) => ({ ...previous, [channelKey]: true }));
              } else {
                event.currentTarget.style.visibility = 'hidden';
              }
            }}
            onLoad={(event) => {
              event.currentTarget.style.visibility = 'visible';
            }}
          />
        </div>
      );
    }

    const logoKey = getChannelLogoKey(name);
    const Logo = CHANNEL_LOGO_MAP[logoKey];

    if (Logo) {
      return (
        <div className="channel-avatar channel-avatar-with-logo">
          <Logo />
        </div>
      );
    }

    return (
      <div className="channel-avatar" style={{ backgroundColor: color }}>
        {getChannelInitials(name)}
      </div>
    );
  };

  const exportToPDF = async () => {
    try {
      await exportContentActivityToPDF({
        trans,
        userInfo,
        currentUser,
        historyByDate,
        summary: model?.summary,
        userTimezone,
        loginUserTimeformat,
      });
    } catch (error) {
      console.error('Error exporting content activity to PDF:', error);
    }
  };

  const renderSubTabs = () => {
    const tabs = [
      { key: CONTENT_ACTIVITY_VIEWS.SUMMARY, label: trans('CONTENT_ACTIVITY_TAB') },
      { key: CONTENT_ACTIVITY_VIEWS.CHARTS, label: trans('CONTENT_CHARTS') },
    ];
    return (
      <div className="contentactivity-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`contentactivity-tab ${activeView === tab.key ? 'active' : ''}`}
            onClick={() => setActiveView(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderStatusBadge = (item) => {
    if (item.status === VIDEO_PLAY_STATUS.COMPLETED) {
      return <span className="status-badge completed">{trans('COMPLETED')}</span>;
    }
    return (
      <span className="status-badge abandoned">
        {`${formatPercent(item.percentWatched)} ${trans('WATCHED')}`}
      </span>
    );
  };

  const renderHistoryRow = (item) => {
    if (item.rowKind !== 'session') {
      // Occurrence-only row: a channel or third-party app was opened, but we
      // have no duration/completion data for it (see normalizeOccurrenceEvent).
      const isApp = item.rowKind === CONTENT_OCCURRENCE_KIND.APP;
      const displayName = isApp ? item.appName : item.channelName;
      return (
        <div key={item.eventId} className="contentactivity-container">
          <div className="contentactivity-row-left">
            {renderChannelAvatar(displayName, item.color)}
            <div className="contentactivity-row-info">
              <div className="contentactivity-video-title">
                {truncateStringWith3Dots(displayName || '—', 60)}
              </div>
            </div>
          </div>
          <div className="contentactivity-row-right">
            <div title={userTimezone} className="contentactivity-eventtime">
              {getTimeFormat(item.ts, userTimezone, loginUserTimeformat?.is24hoursformat)}
            </div>
            <span className="status-badge launched">{trans('CONTENT_LAUNCHED')}</span>
          </div>
        </div>
      );
    }

    return (
      <div key={item.eventId} className="contentactivity-container">
        <div className="contentactivity-row-left">
          {renderChannelAvatar(item.channelName, item.color)}
          <div className="contentactivity-row-info">
            <div className="contentactivity-video-title">
              {truncateStringWith3Dots(item.videoTitle || '—', 60)}
            </div>
          </div>
        </div>
        <div className="contentactivity-row-right">
          <div title={userTimezone} className="contentactivity-eventtime">
            {getTimeFormat(item.ts, userTimezone, loginUserTimeformat?.is24hoursformat)}
          </div>
          <div className="contentactivity-watchduration">{formatWatchDuration(item.watchMs)}</div>
          {renderStatusBadge(item)}
        </div>
      </div>
    );
  };

  const renderHistory = () => {
    if (loading) {
      return (
        <div className="contentactivity-list-container">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={`skeleton_${index}`} className="contentactivity-container skeleton-container">
              <Skeleton active avatar paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
      );
    }
    if (historyEmpty) {
      return (
        <div className="contentactivity-empty-list-container">
          <div className="contentactivity-empty-list-label">{trans('NO_CONTENT_ACTIVITY')}</div>
        </div>
      );
    }
    return (
      <div className="contentactivity-list-container">
        {visibleHistoryByDate.map((group) => {
          const dateLabel =
            group.dateLabel || getDateFormat(group.items[0].ts, userTimezone).dayDateformat;
          return (
            <div key={group.dateKey} className="contentactivite-data-list">
              <div
                className={
                  dateLabel.startsWith('Today')
                    ? 'contentactivity-list-date contentactivity-list-today'
                    : 'contentactivity-list-date'
                }
              >
                <div className="contentactivity-date">{dateLabel}</div>
              </div>
              {group.items.map((item) => renderHistoryRow(item))}
            </div>
          );
        })}
        {hasMoreHistory && (
          <button
            type="button"
            className="contentactivity-view-more"
            onClick={() => setVisibleHistoryCount((count) => count + HISTORY_PAGE_SIZE)}
          >
            {trans('VIEWMORE')}
          </button>
        )}
      </div>
    );
  };

  const renderBody = () => {
    switch (activeView) {
      case CONTENT_ACTIVITY_VIEWS.CHARTS:
        return (
          <ContentActivityCharts
            daily={model?.daily || []}
            hourly={model?.hourly || []}
            channels={model?.channels || []}
            loading={loading}
            loginUserTimeformat={loginUserTimeformat}
          />
        );
      case CONTENT_ACTIVITY_VIEWS.SUMMARY:
      default:
        if (!loading && historyEmpty) {
          return (
            <div className="contentactivity-empty-list-container">
              <div className="contentactivity-empty-list-label">{trans('NO_CONTENT_ACTIVITY')}</div>
            </div>
          );
        }
        return (
          <>
            <ContentActivitySummary summary={model?.summary} loading={loading} />
            <div className="contentactivity-section-header">
              <HistoryOutlined className="contentactivity-section-icon" />
              <h3 className="contentactivity-section-title">{trans('CONTENT_HISTORY')}</h3>
            </div>
            {renderHistory()}
          </>
        );
    }
  };

  const headerSection = () => (
    <div className="contentactivity-header">
      <div className="contentactivity-title">
        <div>
          <ContentActivityIcon className="contentactivity-icon-newui" />
        </div>
        <div className="contentactivity-text-newui">{trans('CONTENTACTIVITY')}</div>
      </div>
      <div className="contentactivity-actions">
        <ExportPdfButton onClick={exportToPDF} disabled={loading || historyEmpty} />
      </div>
    </div>
  );

  const render = (
    <div className="list-contentactivity-container page-content-wrapper">
      {headerSection()}
      {renderSubTabs()}
      <div className="contentactivity-content">{renderBody()}</div>
    </div>
  );

  return (
    <MainPage header={header} noModals={true}>
      {render}
    </MainPage>
  );
};

ListContentActivity.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  model: PropTypes.object,
  loading: PropTypes.bool,
  activeView: PropTypes.string.isRequired,
  setActiveView: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  loginUserTimeformat: PropTypes.object,
  userInfo: PropTypes.object,
};

export default ListContentActivity;
